/**
 * Web Push (VAPID) client helpers.
 * Handles: SW registration, permission prompt, PushManager subscription,
 * and backend sync of the subscription object.
 */
import { api } from "./api";

/** True if the browser supports Web Push at all. iOS Safari only supports
 * it when the PWA is installed to the home screen (iOS 16.4+). */
export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Returns the current permission: "default" | "granted" | "denied". */
export function pushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission;
}

/** Convert a base64url VAPID public key to the Uint8Array the PushManager expects. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Ensure the service worker is registered and return the registration. */
async function ensureRegistration() {
  // Existing registration (app shell already registers /sw.js in index.html)
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  return reg;
}

/** Prompt the user for permission and subscribe to push.
 *  Returns true on success. */
export async function enablePush() {
  if (!pushSupported()) throw new Error("Browser sem suporte a push");

  // 1. Permission
  let perm = Notification.permission;
  if (perm === "default") perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permissão recusada");

  // 2. Public VAPID key from backend
  const { data } = await api.get("/push/vapid-public-key");
  const appServerKey = urlBase64ToUint8Array(data.public_key);

  // 3. Subscribe via PushManager
  const reg = await ensureRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    });
  }

  // 4. Sync to backend
  const raw = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
    user_agent: navigator.userAgent,
  });
  return true;
}

/** Remove the subscription locally and on backend. */
export async function disablePush() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch {
    /* ignore */
  }
  await sub.unsubscribe();
  return true;
}

/** Send a test push (used by the UI "enviar teste" button). */
export async function sendPushTest() {
  const { data } = await api.post("/push/test");
  return data;
}

/** Check if the current device is already subscribed. */
export async function isSubscribed() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}
